import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';

import { getCourseEnrollmentOptions, getCourseEnrollmentQueryKey, updateEnrollmentCompletionDateMutation } from '@drivingdoc/api-client/generated/react-query';

import DateInput from 'components/FormInputs/DateInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getZodObjectDefaults } from 'utils/getZodObjectDefaults';

import FeedbackDialog from '../../FeedbackDialog';
import { UpdateCourseEnrollmentFormValues, UpdateCourseEnrollmentSchema } from './UpdateCourseEnrollmentSchema';

interface UpdateCourseEnrollmentDialogPayload {
  id: string;
}

function UpdateCourseEnrollmentDialog({ payload, open, onClose }: DialogProps<UpdateCourseEnrollmentDialogPayload>) {
  const { id } = payload;

  const dialogs = useDialogs();
  const queryClient = useQueryClient();

  const courseEnrollment = useQuery({
    ...getCourseEnrollmentOptions({
      path: {
        id: id,
      },
    }),

    // don't use cached data
    gcTime: 0,
    enabled: !!id,
  });

  const courseEnrollmentMutation = useMutation({
    ...updateEnrollmentCompletionDateMutation(),
  });

  const form = useForm<UpdateCourseEnrollmentFormValues>({
    mode: 'all',
    resolver: zodResolver(UpdateCourseEnrollmentSchema),
  });

  const { handleSubmit } = form;

  const onSubmit = async ({ completionDate }: UpdateCourseEnrollmentFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you update the course completion date?', {
        okText: 'Confirm',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // update the completion date
      await courseEnrollmentMutation.mutateAsync({
        path: { id },
        body: { completionDate },
      });

      // invalidate the course enrollment query
      await queryClient.invalidateQueries({
        queryKey: getCourseEnrollmentQueryKey({ path: { id } }),
      });

      // close the dialog
      await onClose();

      // show success message
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Course Completion Date Updated',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error updating course completion date',
        message: getApiErrorMessage(error),
      });
    }
  };

  // update form values when the course enrollment is fetched
  useEffect(() => {
    // if the existing enrollment was fetched and the completion date has already been set
    if (courseEnrollment.isSuccess && courseEnrollment.data?.completed_at) {
      // reset the form with the existing completion date
      form.reset({
        completionDate: courseEnrollment.data?.completed_at,
      });
    } else if (courseEnrollment.isSuccess) {
      // reset the form with default values
      form.reset(getZodObjectDefaults(UpdateCourseEnrollmentSchema));
    }
  }, [courseEnrollment.isSuccess, courseEnrollment.data, form]);

  return (
    <Dialog fullWidth open={open} onClose={() => onClose()}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogTitle>Update Course Completion Date</DialogTitle>
          <DialogContent>
            <Grid container rowSpacing={3} columnSpacing={1}>
              <Grid size={12}>
                <DateInput name="completionDate" label="Course Completion Date" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button type="submit" disabled={courseEnrollmentMutation.isPending}>
              Update
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop open={courseEnrollment.isLoading || courseEnrollmentMutation.isPending} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCourseEnrollmentDialog;
